// ProfileCheckCard.jsx — v227.10.0
// Shown on the Describe → Review step, under the Orella bubble and above the section
// cards, when the patient's case (voice + answers) conflicts with their saved profile.
// Each row offers a one-tap "Update profile" (writes through the normal save path) or
// "Keep as-is". Non-blocking — the patient can still continue; a nudge encourages
// resolving first. No amber (this is a data-quality nudge, not a clinical signal).

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardCheck, Check, RotateCcw } from 'lucide-react'
import { useProfileStore } from '../stores'
import { useI18n } from '../i18n'
import { applyDiscrepancy } from '../utils/profileReconcile'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LBLUE = '#A7D9FC'
const ICE = '#E9F8FF'
const MUTED = '#5B6B7B'

export default function ProfileCheckCard({ items = [], resolved = {}, onChange }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { profile, syncSection, updateProfile } = useProfileStore()
  const [busyId, setBusyId] = useState(null)

  const visible = (items || []).filter(Boolean)
  if (visible.length === 0) return null

  const remaining = visible.filter((d) => !resolved[d.id]).length

  const setResolved = (id, choice) => {
    const next = { ...resolved }
    if (choice === null) delete next[id]
    else next[id] = choice
    onChange?.(next)
  }

  const handleUpdate = async (item) => {
    setBusyId(item.id)
    const res = await applyDiscrepancy(item, profile, { syncSection, updateProfile, navigate })
    setBusyId(null)
    // navigated (meds / unmappable) and successful writes both resolve the item.
    if (res?.ok) setResolved(item.id, 'upd')
  }

  return (
    <div className="rounded-2xl overflow-hidden mb-4" style={{ border: `1.5px solid ${LBLUE}`, background: '#fff' }}>
      {/* Header */}
      <div className="flex items-start gap-2.5 px-3.5 py-3" style={{ background: ICE }}>
        <div className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center flex-shrink-0" style={{ background: '#fff', border: `1.5px solid ${LBLUE}` }}>
          <ClipboardCheck size={16} style={{ color: BLUE }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-bold" style={{ color: NAVY }}>{t('profileCheck.title')}</p>
          <p className="text-[11.5px] leading-snug mt-0.5" style={{ color: MUTED }}>
            {t('profileCheck.subtitle')}
          </p>
        </div>
        {remaining > 0 && (
          <span className="text-[10.5px] font-bold rounded-full px-2.5 py-0.5 flex-shrink-0 whitespace-nowrap" style={{ color: BLUE, background: '#fff', border: `1px solid ${LBLUE}` }}>
            {remaining} {t('profileCheck.toReview')}
          </span>
        )}
      </div>

      {/* Items */}
      {visible.map((item) => {
        const choice = resolved[item.id]
        const isBusy = busyId === item.id
        if (choice) {
          const msg = choice === 'upd' ? t('profileCheck.added') : t('profileCheck.keptAsIs')
          return (
            <div key={item.id} className="px-3.5 py-3 flex items-center gap-2" style={{ borderTop: '1px solid #EEF3F8' }}>
              <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#66D170', color: '#fff' }}>
                <Check size={11} strokeWidth={3.5} />
              </span>
              <span className="text-[12px] flex-1 min-w-0" style={{ color: MUTED }}>
                <b style={{ color: NAVY, fontWeight: 600 }}>{item.label}</b> · {msg}
              </span>
              <button onClick={() => setResolved(item.id, null)} className="text-[11px] font-semibold flex items-center gap-1 flex-shrink-0" style={{ color: BLUE }}>
                <RotateCcw size={11} /> {t('profileCheck.undo')}
              </button>
            </div>
          )
        }
        return (
          <div key={item.id} className="px-3.5 py-3" style={{ borderTop: '1px solid #EEF3F8' }}>
            <div className="text-[12.5px] font-bold mb-2 flex items-center gap-1.5" style={{ color: NAVY }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: BLUE }} />
              {item.label}
            </div>
            <div className="flex gap-2 mb-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>{t('profileCheck.youMentioned')}</div>
                <div className="text-[11.5px] leading-snug rounded-[9px] px-2.5 py-2" style={{ background: ICE, border: `1px solid ${LBLUE}`, color: NAVY }}>{item.said}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>{t('profileCheck.yourProfile')}</div>
                <div className="text-[11.5px] leading-snug rounded-[9px] px-2.5 py-2" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', color: MUTED }}>{item.profileValue}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleUpdate(item)}
                disabled={isBusy}
                className="flex-1 rounded-[10px] py-2.5 text-[12px] font-bold text-white flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
                style={{ background: NAVY, opacity: isBusy ? 0.7 : 1 }}
              >
                {isBusy ? <OrellaSpinner size={16} tone="inherit" /> : null}
                {item.section === 'medications' ? t('profileCheck.addMedications') : t('profileCheck.updateProfile')}
              </button>
              <button
                onClick={() => setResolved(item.id, 'keep')}
                disabled={isBusy}
                className="flex-1 rounded-[10px] py-2.5 text-[12px] font-bold active:scale-[0.98] transition-transform"
                style={{ background: '#fff', color: NAVY, border: '1.5px solid #D8E6F0' }}
              >
                {t('profileCheck.keepAsIs')}
              </button>
            </div>
          </div>
        )
      })}

      {/* All resolved */}
      {remaining === 0 && (
        <div className="px-3.5 py-3 flex items-center gap-2.5" style={{ borderTop: '1px solid #EEF3F8', background: '#F3FBF4' }}>
          <span className="w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ background: '#66D170', color: '#fff' }}>
            <Check size={13} strokeWidth={3.5} />
          </span>
          <span className="text-[12.5px] font-semibold" style={{ color: '#15803D' }}>{t('profileCheck.allSet')}</span>
        </div>
      )}
    </div>
  )
}
